import React, {useCallback} from 'react';


import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {GrantResourcesDetails} from 'src/components/grant-resources';
import {UserLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {
    getAssignUsersEntitlementsProgress,
    getAssignUsersEntitlementsResult,
} from 'src/openapi/admin/apis/BulkAssignUsersEntitlementsApi';
import {useAppDispatch} from 'src/store/hooks';

import {Dropdown} from '@pb/kit-component-dropdown';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import {FILE_HEADERS, FILE_NAME, TOOL_TITLE} from './constants';

import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {UpdateUserRelationResult} from 'src/openapi/admin/models/UpdateUserRelationResult';

type Props = {
    id: string,
    onClose: () => void,
};

export const AssignUsersEntitlementsResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();

    const getPaginatedResults = useCallback(
        (bulkResult: UpdateUserRelationResult[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: UpdateUserRelationResult, index) => (
                <Row key={(currentPage - 1) * pageSize + index}>
                    <Cell width={60}>
                        <RowNumbered
                            maxRowNumber={bulkResult?.length || 0}
                            placeholder={(currentPage - 1) * pageSize + index + 1}
                        />
                    </Cell>
                    <Cell>
                        {res && res.userId && res.userEmail ? (
                            <UserLink
                                userId={res.userId}
                                caption={res.userEmail}
                                place=""
                            />
                        ) : null}
                    </Cell>
                    <Cell>
                        {!!res.roleName && (
                            <EllipsisTextHint title={res.roleName}>
                                {res.roleName}
                            </EllipsisTextHint>
                        )}
                    </Cell>
                    <Cell>
                        {res && res.grantResources && !res.roleName ? (
                            <Dropdown
                                hoverEnabled={true}
                                horizontalOffset={80}
                                anchor={({anchorRef, getAnchorProps}) => (
                                    <span ref={anchorRef} {...getAnchorProps()}>
                                        <div className="ellipsis">
                                            {res.grantResources?.resources?.map(({name}) => name).join(', ')}
                                        </div>
                                    </span>
                                )}>
                                <GrantResourcesDetails
                                    grantId={res.grantResources.grantId}
                                    totalCount={res.grantResources.totalCount}
                                />
                            </Dropdown>
                        ) : null}
                    </Cell>
                    <Cell>
                        {!!res.grantName && (
                            <EllipsisTextHint title={res.grantName}>
                                {res.grantName}
                            </EllipsisTextHint>
                        )}
                    </Cell>
                    <Cell><CompleteMark errors={res.errors}/></Cell>
                </Row>
            )),
        [],
    );

    return (
        <BulkActionResultModal<UpdateUserRelationResult>
            id={id}
            onClose={onClose}

            toolTitle={TOOL_TITLE}
            description={bulkResult => (
                <Group justify="space-between">
                    <b>Assigned entitlements</b>
                    <BulkActionDownload
                        toolName={TOOL_TITLE}
                        fileName={FILE_NAME}
                        headers={FILE_HEADERS}
                        startTime={bulkResult.startTime}
                        body={bulkResult.itemList?.map(item => ({
                            userId: item.userId.toString(),
                            email: item.userEmail,
                            role: item.roleName || '',
                            resources: item.grantResources?.resources.map(({name}) => name).join('; '),
                            grantName: item.grantName || '',
                            actionStatus: item.actionStatus,
                            errors: item.errors?.join('. '),
                        })) as Record<string, string>[]}
                    />
                </Group>
            )}
            table={(bulkResult, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={60}><strong>#</strong></Cell>
                        <Cell width={200}>Email</Cell>
                        <Cell width={150}>Role</Cell>
                        <Cell width={150}>Resources</Cell>
                        <Cell>Grant Name</Cell>
                        <Cell width={150}>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(bulkResult, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getAssignUsersEntitlementsProgress({id}))}
            getResult={() => apiDispatch(getAssignUsersEntitlementsResult({id}))}

            sortComparator={(a, b) => a.userEmail.localeCompare(b.userEmail)}
        />
    );
};
